import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Grid, Stack, CircularProgress } from '@mui/material';
import { endPoints } from 'api/endpoints';
import { useMutation } from 'react-query';
import { useRecoilState } from 'recoil';
import Empty from 'components/empty';
import { useParams } from 'react-router-dom';
import { OrderPayload, isAppUrlValid, ssoOktaDialogPopup, uploadedFilesState } from 'pages/sso-okta-modification/store';
import { formOrderObjFromOrderDetails, formUploadObjFromOrderDetails, resetOrder } from 'pages/sso-okta-modification/utils';
import SsoOktaStyles from 'pages/sso-okta/style';
import axios from 'api/request';
import logger from 'utils/logger';

const SsoOktaHeader = React.lazy(() => import('pages/sso-okta-modification/ssoOktaHeader'));
const DialogBox = React.lazy(() => import('pages/sso-okta-modification/components/Dialog-box'));
const CommonForm = React.lazy(() => import('pages/sso-okta-modification/CommonForm'));

const ssoOktaModification = () => {
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [, setAppUrlValid] = useRecoilState(isAppUrlValid);
    const [dialogPopup, setDialogPopup] = useRecoilState(ssoOktaDialogPopup);
    const [, setUploadObj] = useRecoilState(uploadedFilesState);
    const [isLoading, setLoading] = React.useState(false);

    const handleReset = () => {
        setOrder(resetOrder);
        setAppUrlValid(true);
        setDialogPopup(false);
        setUploadObj([]);
    };

    const logoMutation = useMutation((payload) => axios.post(`${endPoints.ssoOkta.applicationLogo}`, payload));

    async function checkCatalog() {
        setLoading(true);
        try {
            if (cartItemId && operation && catalogServiceId) {
                const { data: orderDetails } = await axios.get(`${endPoints.riServices.Jwt.orderDetails}/${cartItemId}`);
                const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails);
                const uploadObj = formUploadObjFromOrderDetails(catalogServiceId, orderDetails);
                let logoArray = [];
                if (uploadObj?.length > 0 && uploadObj[0]?.id && uploadObj[0]?.blobRelativeLink) {
                    try {
                        const logoResponse = await logoMutation.mutateAsync({
                            id: uploadObj[0]?.id,
                            blobRelativeLink: uploadObj[0]?.blobRelativeLink,
                        });

                        if (logoResponse instanceof Blob) {
                            logoResponse.text().then((text) => {
                                const json = JSON.parse(text);
                                uploadObj[0].href = `data:image/png;base64,${json?.data}`;
                                logoArray = [...uploadObj];
                            });
                        }
                    } catch (err) {
                        logger.error('Error fetching applicationLogoApi:', err);
                    }
                }
                if (!uploadObj?.length) {
                    const appClientId = orderObj?.appClientId || order?.appClientId;
                    const environment = orderObj?.environment || order?.environment;
                    const requestType = orderObj?.requestType || order?.requestType;
                    if (!appClientId) return;

                    try {
                        const { data } = await axios.get(endPoints.ssoOkta.appClientID(appClientId, environment, requestType));
                        const uploadResponse = data?.filesUploadResponse?.uploadResponses?.[0];
                        if (uploadResponse?.href) {
                            const { href: logoHref, fileName: name = '', fileSize: size = 0 } = uploadResponse;
                            orderObj.logoHref = logoHref;
                            logoArray = [{ name, href: logoHref, size }];
                        }
                    } catch (err) {
                        logger.error('Error fetching appClientIdApi for logo:', err);
                    }
                }
                const response = await axios.get(`${endPoints?.ssoOkta?.applicationOwner(orderObj?.businessApplication)}`);
                const updatedOrderObj = {
                    ...orderObj,
                    appOwnerDetails: {
                        name: response?.data?.owner,
                        gpid: response?.data?.gpId,
                        email: response?.data?.email,
                    },
                    appOwnerText: `${response?.data?.gpId} - ${response?.data?.owner}` || null,
                };
                setUploadObj(logoArray);
                setAppUrlValid(true);
                setOrder(() => ({
                    ...order,
                    ...updatedOrderObj,
                }));
            } else {
                setOrder((previous) => ({
                    ...previous,
                    isTouched: false,
                }));
            }
            setLoading(false);
        } catch (err) {
            setLoading(false);
        }
    }

    React.useEffect(() => {
        if (catalogCode) {
            handleReset();
            checkCatalog();
        }
    }, [catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => handleReset();
        // eslint-disable-next-line
    }, []);
    return (
        <>
            <Helmet>
                <title>SSO Integration</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={SsoOktaStyles.ssoOkta_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={SsoOktaStyles.circularProgressColor} />
                    </Box>
                }
            >
                {isLoading && (
                    <Stack sx={SsoOktaStyles?.emptyStack}>
                        <Empty isLoading color="#000000" msg="Loading..." />
                    </Stack>
                )}
                {!isLoading && (
                    <>
                        <SsoOktaHeader />
                        <Grid item container spacing={2}>
                            <Grid item xs={12}>
                                <Grid display="flex" flexDirection="column" padding={4} gap={3} className="contentContainer">
                                    <CommonForm />
                                </Grid>
                            </Grid>
                        </Grid>
                        <DialogBox isOpen={dialogPopup} reset={() => handleReset()} redirectUrl="/" operation="success" />
                    </>
                )}
            </Suspense>
        </>
    );
};

export default ssoOktaModification;
